// ante-react-kit/src/components/v2/hooks/useVoucherActions.js
import { useCallback, useRef } from 'react';

export default function useVoucherActions(voucherData, autoReload) {
    // voucherData: return value of useVoucherData
    // autoReload: return value of useAutoReload

    var {
        mainData, mainDataRef, mainCodeRef, mainIDRef,
        setMainData, setError, mainState,
        setFlowData, setFlowVisible,
        loadContent, navigateHome, navigateToReload, reload,
        session, quickReloadTimerRef
    } = voucherData;

    var propsRef = useRef(null); // Set by AntVoucherV2 — holds current props

    function getProps() {
        return propsRef.current || {};
    }

    // --- showAlert ---
    var msgBoxRef = useRef(null); // Will be set by AntVoucherV2
    var showAlert = useCallback(function showAlert(args, onClosedCallback) {
        if (msgBoxRef.current) msgBoxRef.current.show(args, onClosedCallback);
    }, []);

    // --- showPopup ---
    var showPopup = useCallback(function showPopup(ref, withMain, defaultObj) {
        if (!ref) return;

        var obj = {};
        if (defaultObj && typeof defaultObj === 'object') obj = Object.assign({}, obj, defaultObj);

        if (!withMain) {
            if (ref.show) ref.show(obj);
            else if (ref.current && ref.current.show) ref.current.show(obj);
            return;
        }

        var code = mainCodeRef.current;
        var id = mainIDRef.current;
        if (id || code) {
            var main = mainData;
            if (main && typeof main === 'object') obj = Object.assign({}, main, obj);

            if (ref.show) ref.show(obj);
            else if (ref.current && ref.current.show) ref.current.show(obj);
        }
    }, [mainData]);

    // --- postAction (unified) ---
    var postAction = useCallback(async function postAction(action, code, id) {
        var p = getProps();
        if (!code) code = mainCodeRef.current;
        if (!id) id = mainIDRef.current;
        if (!id && !code) return;

        autoReload.stop();

        var useQuickResponse = session && session.isSocketConnected ? session.isSocketConnected() : false;

        try {
            var result = await p.API.postAction(action, code, id, useQuickResponse, mainDataRef.current);
            if (result && result.error) {
                showAlert({ content: result.error });
                return result;
            }

            if (p.onActionPosted) p.onActionPosted(action, result);

            if (action === 'delete') {
                navigateHome();
                return;
            }
            if (action === 'close') {
                if (mainCodeRef.current === code || mainIDRef.current === id) await reload({ refreshMode: 'allPage' });
                return result;
            }
            if (action === 'print') {
                return result.data;
            }

            var actionData = result && result.data;
            var hasActionFlow = Boolean(
                p.flow &&
                    actionData &&
                    Object.prototype.hasOwnProperty.call(actionData, 'voucherFlow') &&
                    (!mainCodeRef.current || !actionData.code || actionData.code === mainCodeRef.current) &&
                    (!mainIDRef.current || !actionData.id || String(actionData.id) === String(mainIDRef.current)) &&
                    (!actionData.updated_at || !mainDataRef.current?.updated_at || actionData.updated_at >= mainDataRef.current.updated_at)
            );
            if (hasActionFlow) {
                var nextFlow = actionData.voucherFlow || null;
                var hasFlowNodes = nextFlow && nextFlow.nodes && nextFlow.nodes.length > 1;
                setFlowData(nextFlow);
                setFlowVisible(Boolean(hasFlowNodes && p.flow.display === 'always'));
                actionData = { ...actionData };
                delete actionData.voucherFlow;
            }

            if (actionData) {
                setMainData(function (v) {
                    var newData = JSON.parse(JSON.stringify(v));
                    var isQuickResponse = actionData.isQuickResponse && useQuickResponse;
                    if (!isQuickResponse) {
                        Object.keys(newData).forEach(function (key) {
                            if (key !== 'lookups') delete newData[key];
                        });
                        newData.actions = [];
                        newData.edit = {
                            isEditable: false,
                            withChildren: [],
                            editable_fields: []
                        };
                    }
                    var next = Object.assign(newData, actionData);
                    if (isQuickResponse) {
                        if (v.actions) next.actions = v.actions;
                        if (v.edit) next.edit = v.edit;
                    }
                    mainDataRef.current = next;
                    return next;
                });
            }

            if (!actionData || !actionData.isQuickResponse || !useQuickResponse) {
                if (!(p.flow && !hasActionFlow) && (mainCodeRef.current === code || mainIDRef.current === id)) {
                    setTimeout(function () { autoReload.start(false); }, 1000);
                }
            } else {
                if (quickReloadTimerRef.current) clearTimeout(quickReloadTimerRef.current);
                quickReloadTimerRef.current = setTimeout(function () {
                    quickReloadTimerRef.current = null;
                    loadContent({ isRefresh: true });
                }, 1200);
            }

            if (p.flow && !hasActionFlow && (mainCodeRef.current === code || mainIDRef.current === id)) {
                if (quickReloadTimerRef.current) {
                    clearTimeout(quickReloadTimerRef.current);
                    quickReloadTimerRef.current = null;
                }
                await loadContent({ recalcable: true, isRefresh: true, forcePrepare: true, bypassSnapshot: true });
            }

            return result;
        } catch (e) {
            showAlert({ content: e.message || 'Action error' });
            return null;
        }
    }, [mainData, autoReload, setMainData, setFlowData, setFlowVisible, showAlert, navigateHome, reload, session, loadContent, mainDataRef, quickReloadTimerRef]);

    // --- quickCreate ---
    var quickCreate = useCallback(async function quickCreate() {
        var p = getProps();
        try {
            var result = await p.API.postQuickCreate();
            if (result && result.error) {
                showAlert({ content: result.error });
            } else if (result && result.data) {
                navigateToReload(result.data.id, result.data.code);
            }
        } catch (e) {
            showAlert({ content: e.message || 'Create error' });
        }
    }, [navigateToReload, showAlert]);

    // --- clone ---
    var clone = useCallback(async function clone() {
        var p = getProps();
        try {
            var result = await p.API.postClone(mainCodeRef.current, mainIDRef.current);
            if (result && result.error) {
                showAlert({ content: result.error });
            } else if (result && result.data) {
                navigateToReload(result.data.id, result.data.code);
            }
        } catch (e) {
            showAlert({ content: e.message || 'Clone error' });
        }
    }, [navigateToReload, showAlert]);

    return {
        propsRef: propsRef,
        msgBoxRef: msgBoxRef,
        showPopup: showPopup,
        showAlert: showAlert,
        postAction: postAction,
        quickCreate: quickCreate,
        clone: clone
    };
}
